import { inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CALCULATION_ID } from '@marmon/frontend/common';
import {
	Analysis,
	AnalysisCalculation,
	CalculationType,
	decodeSegmentSequence,
	DefaultCalculationType,
	encodeSegmentSequence,
} from '@marmon/shared/types/analysis';
import { coerceCurrency } from '@marmon/shared/types/common';
import {
	BehaviorSubject,
	catchError,
	combineLatest,
	debounceTime,
	filter,
	of,
	switchMap,
	tap,
} from 'rxjs';
import { AnalysisApiService } from './analysis-api.service';

type CalculationFilter = {
	segments: string[];
	calculationType: CalculationType;
};

@Injectable({ providedIn: 'root' })
export class AnalysisStateService {
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);

	private readonly analysisApiService = inject(AnalysisApiService);
	private readonly calculationId$ = inject(CALCULATION_ID);

	readonly loading$ = new BehaviorSubject<boolean>(true);

	/**
	 * The main data object that drives all of the other calculations. The analysis
	 * is the single most important object to perform the calculations. Without it,
	 * we cannot determine anything.
	 */
	readonly analysis$ = new BehaviorSubject<Analysis | null>(null);

	/**
	 * Display currency for the open analysis. Money formatting reads this so
	 * symbols follow the analysis without converting stored cents.
	 */
	readonly currency = signal(coerceCurrency(null));

	/**
	 * The current calculations to be referenced through the UI for the given parameters
	 * and analysis selected set by the user.
	 */
	readonly calculation$: BehaviorSubject<AnalysisCalculation | null> =
		new BehaviorSubject<AnalysisCalculation | null>(null);

	/**
	 * The calculation and analysis filters that are globally applied to the dataset
	 */
	readonly filters$ = new BehaviorSubject<CalculationFilter>({
		segments: [],
		calculationType: DefaultCalculationType,
	});

	/**
	 * Update stream that triggers once the analysis has been updated.
	 * Responsible for gathering and calculating most of the analysis derived values
	 * in the app.
	 *
	 * @todo stream against the possible filters that can be changed to fetch new
	 * calculations.
	 */
	readonly stream$ = combineLatest([
		this.filters$.pipe(catchError(() => of(null))),
		this.analysis$.pipe(
			filter((analysis) => !!analysis),
			catchError(() => of(null)),
		),
	]).pipe(
		debounceTime(0),
		tap(() => this.loading$.next(true)),
		switchMap(([filters, analysis]) => {
			if (!analysis || !filters) {
				return Promise.resolve({
					calculation: null,
					state: 'error',
				});
			}

			// try/catch for fallback to avoid stream cancellation due to error
			try {
				const { segments, calculationType } = filters;
				const segmentation = this.getSegmentation(segments);
				const segmentSequence = encodeSegmentSequence(segmentation);

				return this.analysisApiService
					.findOrCreateCalculation({
						type: calculationType,
						segmentSequence,
						analysis,
					})
					.catch(() => ({
						state: 'error',
						calculation: null,
					}));
			} catch (error) {
				console.error('Error processing the calculation', error);
				return Promise.resolve({ calculation: null, state: 'error' });
			}
		}),
		tap((resolve) => {
			if (resolve.state === 'cached') {
				// if it's cached we don't need to wait for user to load, just load it
				this.applyCalculation(resolve.calculation);
			}

			this.loading$.next(false);
		}),
	);

	/**
	 * Quick getter to the current analysis value in the state
	 * @returns the stored analysis value
	 */
	get analysis() {
		return this.analysis$.value;
	}

	/**
	 * Quick getter to the current calculation value in the state
	 * @returns the stored calculation value
	 */
	get calculation(): AnalysisCalculation | null {
		return this.calculation$.value;
	}

	/**
	 * Updates the analysis value with the requested one
	 *
	 * @param value the analysis value to update the state service to
	 * @param calculation provide a calculation to preload the analysis with, if none is provided then it will reset
	 */
	update(
		value: Analysis | null,
		calculation?: AnalysisCalculation,
		options?: { updateRoute?: boolean; force?: boolean },
	) {
		const { updateRoute, force } = {
			updateRoute: false,
			force: false,
			...options,
		};

		if (!force && this.analysis?.id === value?.id) return;

		this.currency.set(coerceCurrency(value?.currency));
		this.applyCalculation(calculation ?? null, { updateRoute });

		if (calculation && value) {
			let segments = value.segments;
			if (calculation.segmentSequence) {
				segments = Array.from(
					decodeSegmentSequence(
						calculation.segmentSequence,
						value.segments,
					).entries(),
				).reduce((value, [segment, enabled]) => {
					if (enabled) value.push(segment);
					return value;
				}, [] as string[]);
			}

			this.setFilters({
				calculationType: calculation.type,
				segments,
			});
		}

		this.analysis$.next(value);
	}

	/**
	 * Sets the analysis state filters that affect what the calculations will be
	 *
	 * @param options the filter options specific to the analysis state
	 */
	readonly setFilters = (options: {
		segments?: string[];
		calculationType?: CalculationType;
	}) => this.filters$.next({ ...this.filters$.value, ...options });

	/**
	 * Sets the segmentation value with the new segments that have been changed.
	 *
	 * @param segments the segments to override the current value with
	 * @returns the newly set segmentation value
	 */
	private getSegmentation(segments: string[]) {
		const map = new Map<string, boolean>();

		const analysis = this.analysis;
		analysis?.segments.forEach((segment) =>
			map.set(segment, segments.includes(segment)),
		);

		return map;
	}

	applyCalculation(
		calculation: AnalysisCalculation | null,
		options?: { updateRoute: boolean },
	) {
		this.calculation$.next(calculation);
		this.calculationId$.next(calculation?.id ?? null);

		const { updateRoute } = { updateRoute: true, ...options };
		if (updateRoute && calculation) {
			// Only the app-level default calculation (a full-segment `revenue`
			// calc, whose id equals `DefaultCalculationType`) is what the resolver
			// loads when no `calculation` query param is present. Every other
			// calculation — including the full-segment `margin` calc, whose id is
			// `'margin'` — must be encoded in the URL so it survives a reload.
			// Note: `isDefaultCalculation` treats both `revenue` and `margin` as
			// defaults, so it must NOT be used for this route decision.
			const queryParams =
				calculation.id !== DefaultCalculationType
					? { calculation: calculation.id }
					: undefined;

			this.router.navigate([], {
				queryParams,
				queryParamsHandling: 'replace',
				relativeTo: this.route,
			});
		}
	}
}
